import axios, {AxiosRequestConfig, AxiosResponse} from "axios";
import {Configs} from "../configs";
import {AlertType} from "../model/enum/alert.enum";
import {ApiBody} from "../model/interface/api.body.interface";
import {MessageBus} from "./messageBus";
import router from "../router/router";
import store from "../store/store";

export class ApiService {

    private static instance: ApiService;
    private static serverURL: string;
    private static socketURL: string;

    /**
     * URL-Strings
     */
    /* Test */
    private readonly getTestAppendix: string = "test";

    /* Client */
    private readonly clientURL: string = "user";
    private readonly getLoginAppendix: string = "login";
    private readonly getLogoutAppendix: string = "logout";
    private readonly getAllClientsAppendix: string = "all";
    private readonly getAllClientsMetaAppendix: string = "meta";
    private readonly getClientDeleteAppendix: string = "delete";
    private readonly getRolesAppendix: string = "roles";

    /* Project */
    private readonly projectURL: string = "project";
    private readonly getAllProjectsAppendix: string = "all";
    private readonly getAllProjectsMetaDataAppendix: string = "all/metadata";
    private readonly postProjectsAppendix: string = "add";

    /* Author Whitelist */
    private readonly whitelistAppendix: string = "whitelist";
    private readonly getWhitelistDropAppendix: string = "drop";
    private readonly getAllWhitelistAppendix: string = "all";
    private readonly postWhitelistAppendix: string = "add";
    private readonly postWhitelistManyAppendix: string = "many";

    /* Admin */
    private readonly adminURL: string = "admin";
    private readonly isAdminAppendix: string = "isadmin";
    private readonly clientWhitelistAppendix: string = "whitelist";
    private readonly clientAdminAppendix: string = "set";

    /* Migration */
    private readonly migrationURL: string = "job";
    private readonly triggerAppendix: string = "trigger";
    private readonly logsAppendix: string = "logs";

    /* Base URL */
    private readonly baseURLURL: string = "baseurl";

    private constructor() {
        ApiService.serverURL = Configs.getInstance().getConfigs().SERVER_URL;
    }

    /**
     * Returns the instacne
     */
    public static getInstance() {
        if (ApiService.instance) {
            return ApiService.instance;
        } else {
            ApiService.instance = new ApiService();
            return ApiService.instance;
        }
    }

    public async postWithConfig(url: string, body: ApiBody, config?: any) {
        let result: AxiosResponse<any>;
        try {
            result = await axios.post(url, body, config);
            return result;
        } catch (e) {
            this.handleError(e);
            return false;
        }
    }

    public async post(url: string, body: ApiBody) {
        let result: AxiosResponse<any>;
        try {
            result = await axios.post(url, body, store.getters.authentificationConfig);
            return result;
        } catch (e) {
            this.handleError(e);
            return false;
        }
    }

    public async getUnhandled(url: string) {
        let result: AxiosResponse<any>;
        try {
            result = await axios.get(url, store.getters.authentificationConfig);
            return result;
        } catch (e) {
            return false;
        }
    }

    public async get(url: string) {
        let result: AxiosResponse<any>;
        try {
            result = await axios.get(url, store.getters.authentificationConfig);
            return result;
        } catch (e) {
            this.handleError(e);
            return false;
        }
    }

    public async put(url: string, body: any) {
        let result: AxiosResponse<any>;
        try {
            result = await axios.put(url, body, store.getters.authentificationConfig);
            return result;
        } catch (e) {
            this.handleError(e);
            return false;
        }
    }

    public async delete(url: string) {
        let result: AxiosResponse<any>;
        try {
            result = await axios.delete(url, store.getters.authentificationConfig);
            return result;
        } catch (e) {
            this.handleError(e);
            return false;
        }
    }

    public async deleteWithPayload(url: string, body: any) {
        let result: AxiosResponse<any>;
        try {
            result = await axios.delete(url, {
                headers: store.getters.authentificationConfig.headers,
                data: body
            });
            return result;
        } catch (e) {
            this.handleError(e);
            return false;
        }
    }

    public getProjectsAllURL(): string {
        return `${ApiService.serverURL}/${ApiService.getInstance().projectURL}/${this.getAllProjectsAppendix}`;
    }

    public getProjectAllMetaURL(): string {
        return `${ApiService.serverURL}/${ApiService.getInstance().projectURL}/${this.getAllProjectsMetaDataAppendix}`;
    }

    public getProjectURL(projectId: number): string {
        return `${ApiService.serverURL}/${ApiService.getInstance().projectURL}/${projectId}`;
    }

    public getPostProjectURL(): string {
        return `${ApiService.serverURL}/${ApiService.getInstance().projectURL}/${this.postProjectsAppendix}`;
    }

    public getWhitelistURL(projectId: number): string {
        return `${ApiService.serverURL}/${ApiService.getInstance().projectURL}/${projectId}/${this.whitelistAppendix}/${this.getAllWhitelistAppendix}`;
    }

    public getWhitelistAddURL(projectId: number): string {
        return `${ApiService.serverURL}/${ApiService.getInstance().projectURL}/${projectId}/${this.whitelistAppendix}/${this.postWhitelistAppendix}`;
    }

    public getWhitelistAddMultiURL(projectId: number): string {
        return `${ApiService.serverURL}/${ApiService.getInstance().projectURL}/${projectId}/${this.whitelistAppendix}/${this.postWhitelistAppendix}/${this.postWhitelistManyAppendix}`;
    }

    public getWhitelistDeleteURL(projectId: number, authorId: number): string {
        return `${ApiService.serverURL}/${ApiService.getInstance().projectURL}/${projectId}/${this.whitelistAppendix}/${authorId}`;
    }

    public getWhitelistDropURL(projectId: number): string {
        return `${ApiService.serverURL}/${ApiService.getInstance().projectURL}/${projectId}/${this.whitelistAppendix}/${this.getWhitelistDropAppendix}`;
    }

    public getWebsocketURL() {
        return ApiService.socketURL;
    }

    public getLoginURL() {
        return `${ApiService.serverURL}/${this.getLoginAppendix}`;
    }

    public getLogoutURL() {
        return `${ApiService.serverURL}/${this.getLogoutAppendix}`;
    }

    public getClientURL() {
        return `${ApiService.serverURL}/${this.clientURL}`;
    }
    public getClientDeleteURL(clientName: string) {
        return `${ApiService.serverURL}/${this.clientURL}/${this.getClientDeleteAppendix}/${clientName}`;
    }
    public getReadAllClientsURL() {
        return `${ApiService.serverURL}/${this.clientURL}/${this.getAllClientsAppendix}`;
    }
    public getReadAllClientsMetaURL() {
        return `${ApiService.serverURL}/${this.clientURL}/${this.getAllClientsAppendix}/${this.getAllClientsMetaAppendix}`;
    }
    public getTestURL() {
        return `${ApiService.serverURL}/${this.getTestAppendix}`;
    }

    public getClientSwitchWhitelistURL(username: string) {
        return `${ApiService.serverURL}/${this.adminURL}/${this.clientWhitelistAppendix}/${username}`;
    }

    public getSwitchClientAdminURL(username: string) {
        return `${ApiService.serverURL}/${this.adminURL}/${this.clientAdminAppendix}/${username}`;
    }

    public getTriggerMigrationURL(projectId: number) {
        return `${ApiService.serverURL}/${this.migrationURL}/${this.triggerAppendix}/${projectId}`;
    }

    public getRolesURL() {
        return `${ApiService.serverURL}/${this.clientURL}/${this.getRolesAppendix}`;
    }

    public getProjectWithJobLogsURL(logId: number) {
        return `${ApiService.serverURL}/${this.migrationURL}/${this.logsAppendix}/${logId}`;
    }
    public getBaseURLURL() {
        return `${ApiService.serverURL}/${this.baseURLURL}`;
    }
    public getIsAdminURL() {
        return `${ApiService.serverURL}/${this.clientURL}/${this.isAdminAppendix}`;
    }

    public handleError(e: any) {
        if (e.response && e.response.status) { //Session expired
            switch (e.response.status) {
                case 401:
                case 403:
                    router.push('/permission-denied');
                    break;
                case 404:
                    MessageBus.$emit(AlertType[AlertType.error], 'Not found!');
                    break;
                case 409:
                    MessageBus.$emit(AlertType[AlertType.error], 'Already exists! ' + e.response.data);
                    break;
                case 500:
                    console.dir(e);
                    MessageBus.$emit(AlertType[AlertType.error], 'Error! ' + e.response.data.message);
                    break;
            }
        } else {
            console.error(e);
            MessageBus.$emit(AlertType[AlertType.error], e.message);
        }
    }
}